import type { CSSProperties } from 'react';

export type PromoVisualTheme = {
  background: string;
  borderColor: string;
  boxShadow: string;
  iconBackground: string;
  iconBorderColor: string;
  iconColor: string;
  trackColor: string;
  fill: string;
  actionBackground: string;
  accentColor: string;
  accentSoft: string;
  accentMuted: string;
  surfaceBackground: string;
  onFillColor: string;
  detailsCardFrameStyle?: CSSProperties;
  edgeGlintStyle: CSSProperties;
  edgeGlintVerticalStyle: CSSProperties;
  sparkleStyle: CSSProperties;
};

const makeEdgeGlint = (color: string): CSSProperties => ({
  background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
  boxShadow: `0 0 10px ${color}`
});

const makeVerticalEdgeGlint = (color: string): CSSProperties => ({
  background: `linear-gradient(180deg, transparent, ${color}, transparent)`,
  boxShadow: `0 0 10px ${color}`
});

const makeSparkle = (color: string, glow: string): CSSProperties => ({
  background: color,
  boxShadow: `0 0 8px ${color}, 0 0 14px ${glow}`
});

const promoThemes: Record<string, PromoVisualTheme> = {
  weekly_qualification_freeroll: {
    background:
      'linear-gradient(135deg, rgba(54, 150, 118, 0.96) 0%, rgba(34, 104, 103, 0.9) 54%, rgba(22, 44, 48, 0.96) 100%)',
    borderColor: 'rgba(135, 241, 205, 0.32)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.15), 0 8px 22px rgba(0,0,0,0.28), 0 0 18px rgba(54, 150, 118, 0.16)',
    iconBackground: 'rgba(9, 30, 28, 0.32)',
    iconBorderColor: 'rgba(173, 245, 222, 0.22)',
    iconColor: '#e0fff5',
    trackColor: 'rgba(18, 62, 59, 0.52)',
    fill: 'linear-gradient(90deg, #d4fff1, #57d7bb)',
    actionBackground: 'rgba(9, 31, 32, 0.56)',
    accentColor: '#57d7bb',
    accentSoft: 'rgba(87, 215, 187, 0.18)',
    accentMuted: 'rgba(135, 241, 205, 0.28)',
    surfaceBackground: 'rgba(23, 70, 65, 0.2)',
    onFillColor: '#0c2e2a',
    edgeGlintStyle: makeEdgeGlint('rgba(196, 255, 235, 0.94)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(196, 255, 235, 0.94)'),
    sparkleStyle: makeSparkle('#d4fff1', 'rgba(87, 215, 187, 0.45)')
  },
  tournament_tier_qualification: {
    background:
      'linear-gradient(135deg, rgba(67, 119, 191, 0.96) 0%, rgba(34, 84, 128, 0.9) 55%, rgba(21, 35, 58, 0.96) 100%)',
    borderColor: 'rgba(147, 201, 255, 0.35)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.16), 0 8px 22px rgba(0,0,0,0.28), 0 0 18px rgba(67, 119, 191, 0.16)',
    iconBackground: 'rgba(10, 18, 31, 0.32)',
    iconBorderColor: 'rgba(202, 229, 255, 0.24)',
    iconColor: '#e8f4ff',
    trackColor: 'rgba(28, 46, 67, 0.5)',
    fill: 'linear-gradient(90deg, #d7efff, #69b7ff)',
    actionBackground: 'rgba(12, 26, 43, 0.56)',
    accentColor: '#69b7ff',
    accentSoft: 'rgba(105, 183, 255, 0.18)',
    accentMuted: 'rgba(147, 201, 255, 0.28)',
    surfaceBackground: 'rgba(28, 55, 92, 0.22)',
    onFillColor: '#0a2238',
    edgeGlintStyle: makeEdgeGlint('rgba(202, 229, 255, 0.96)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(202, 229, 255, 0.96)'),
    sparkleStyle: makeSparkle('#d7efff', 'rgba(105, 183, 255, 0.45)')
  },
  hybrid_freeroll_qualification: {
    background:
      'linear-gradient(135deg, rgba(233, 140, 38, 0.95) 0%, rgba(185, 77, 30, 0.88) 56%, rgba(63, 42, 29, 0.96) 100%)',
    borderColor: 'rgba(255, 205, 126, 0.35)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 22px rgba(0,0,0,0.28), 0 0 18px rgba(233, 140, 38, 0.14)',
    iconBackground: 'rgba(17, 24, 31, 0.28)',
    iconBorderColor: 'rgba(255,255,255,0.22)',
    iconColor: '#ffffff',
    trackColor: 'rgba(79, 62, 51, 0.46)',
    fill: 'linear-gradient(90deg, #fff1b6, #ffd15a)',
    actionBackground: 'rgba(20, 24, 30, 0.54)',
    accentColor: '#E98C26',
    accentSoft: 'rgba(233, 140, 38, 0.18)',
    accentMuted: 'rgba(255, 205, 126, 0.28)',
    surfaceBackground: 'rgba(100, 55, 30, 0.2)',
    onFillColor: '#1f1206',
    edgeGlintStyle: makeEdgeGlint('rgba(255, 246, 203, 0.96)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(255, 246, 203, 0.96)'),
    sparkleStyle: makeSparkle('#fff1b6', 'rgba(255, 209, 90, 0.45)')
  },
  wanted_poster: {
    background: 'linear-gradient(180deg, #e9d5a3 0%, #d4b478 100%)',
    borderColor: '#4a230a',
    boxShadow:
      '0 0 0 1px rgba(74,35,10,0.4), 0 6px 16px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,235,200,0.6)',
    iconBackground: 'linear-gradient(180deg, #ffe28a 0%, #e8b94a 45%, #a67514 100%)',
    iconBorderColor: '#1a0e06',
    iconColor: '#3b2008',
    trackColor: 'rgba(74,35,10,0.25)',
    fill: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 5px, transparent 5px 10px), linear-gradient(180deg, #b85913, #4a230a)',
    actionBackground: 'rgba(74, 35, 10, 0.18)',
    accentColor: '#E98C26',
    accentSoft: 'rgba(233, 140, 38, 0.18)',
    accentMuted: 'rgba(255, 205, 126, 0.28)',
    surfaceBackground: 'rgba(100, 55, 30, 0.2)',
    onFillColor: '#fff4d3',
    detailsCardFrameStyle: {
      border: '1px dashed rgba(74, 35, 10, 0.5)',
      borderRadius: 5,
      inset: 4,
      pointerEvents: 'none',
      position: 'absolute',
      zIndex: 2
    },
    edgeGlintStyle: makeEdgeGlint('rgba(255, 235, 170, 0.74)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(255, 235, 170, 0.74)'),
    sparkleStyle: makeSparkle('#fff1b6', 'rgba(108, 68, 12, 0.35)')
  },
  western_orange_details: {
    background:
      'radial-gradient(ellipse 800px 500px at 70% 0%, rgba(232,130,31,0.12), transparent 60%), radial-gradient(ellipse 700px 500px at 0% 100%, rgba(31,63,99,0.4), transparent 60%), linear-gradient(180deg, #0e1a2d 0%, #0a1322 100%)',
    borderColor: 'rgba(232, 130, 31, 0.3)',
    boxShadow:
      'inset 0 1px 0 rgba(255,220,160,0.18), inset 0 -1px 0 rgba(0,0,0,0.35), 0 6px 18px rgba(0,0,0,0.44), 0 0 18px rgba(232,130,31,0.12)',
    iconBackground: 'rgba(24, 12, 8, 0.58)',
    iconBorderColor: 'rgba(232, 130, 31, 0.38)',
    iconColor: '#f1d3a1',
    trackColor: 'rgba(16, 8, 5, 0.62)',
    fill: 'linear-gradient(180deg, #ffd089 0%, #f59838 52%, #c46410 100%)',
    actionBackground: 'rgba(12, 7, 5, 0.56)',
    accentColor: '#e8821f',
    accentSoft: 'rgba(232, 130, 31, 0.16)',
    accentMuted: 'rgba(232, 130, 31, 0.34)',
    surfaceBackground: 'rgba(232, 130, 31, 0.12)',
    onFillColor: '#241206',
    edgeGlintStyle: makeEdgeGlint('rgba(255, 200, 140, 0.76)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(255, 200, 140, 0.76)'),
    sparkleStyle: makeSparkle('#ffd089', 'rgba(232, 130, 31, 0.38)')
  },
  weekly_timeback_credit: {
    background:
      'linear-gradient(135deg, rgba(145, 86, 193, 0.95) 0%, rgba(105, 61, 147, 0.9) 55%, rgba(40, 28, 60, 0.96) 100%)',
    borderColor: 'rgba(223, 191, 255, 0.34)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.15), 0 8px 22px rgba(0,0,0,0.28), 0 0 18px rgba(145, 86, 193, 0.16)',
    iconBackground: 'rgba(25, 15, 39, 0.34)',
    iconBorderColor: 'rgba(231, 210, 255, 0.22)',
    iconColor: '#f7edff',
    trackColor: 'rgba(64, 42, 90, 0.52)',
    fill: 'linear-gradient(90deg, #f4dcff, #c58aff)',
    actionBackground: 'rgba(28, 17, 43, 0.56)',
    accentColor: '#c58aff',
    accentSoft: 'rgba(197, 138, 255, 0.18)',
    accentMuted: 'rgba(223, 191, 255, 0.28)',
    surfaceBackground: 'rgba(69, 43, 102, 0.22)',
    onFillColor: '#2a1240',
    edgeGlintStyle: makeEdgeGlint('rgba(240, 219, 255, 0.95)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(240, 219, 255, 0.95)'),
    sparkleStyle: makeSparkle('#f4dcff', 'rgba(197, 138, 255, 0.45)')
  },
  // Weekly Cash Back money green (EN-11291), the `cash` preset.
  weekly_cash_back: {
    background:
      'linear-gradient(135deg, rgba(31, 138, 94, 0.95) 0%, rgba(19, 94, 66, 0.92) 55%, rgba(9, 36, 28, 0.96) 100%)',
    borderColor: 'rgba(168, 240, 203, 0.34)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.15), 0 8px 22px rgba(0,0,0,0.28), 0 0 18px rgba(31, 138, 94, 0.18)',
    iconBackground: 'rgba(8, 32, 24, 0.36)',
    iconBorderColor: 'rgba(200, 245, 220, 0.22)',
    iconColor: '#ecfff5',
    trackColor: 'rgba(16, 64, 46, 0.55)',
    fill: 'linear-gradient(90deg, #dcffea, #5fe3a1)',
    actionBackground: 'rgba(8, 36, 26, 0.56)',
    accentColor: '#5fe3a1',
    accentSoft: 'rgba(95, 227, 161, 0.18)',
    accentMuted: 'rgba(168, 240, 203, 0.28)',
    surfaceBackground: 'rgba(24, 92, 66, 0.22)',
    onFillColor: '#0b2e21',
    edgeGlintStyle: makeEdgeGlint('rgba(220, 255, 234, 0.95)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(220, 255, 234, 0.95)'),
    sparkleStyle: makeSparkle('#dcffea', 'rgba(95, 227, 161, 0.45)')
  },
  legacy: {
    background:
      'linear-gradient(135deg, rgba(88, 105, 125, 0.95) 0%, rgba(55, 71, 90, 0.92) 56%, rgba(24, 32, 44, 0.96) 100%)',
    borderColor: 'rgba(204, 220, 238, 0.25)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.13), 0 8px 22px rgba(0,0,0,0.28), 0 0 18px rgba(88, 105, 125, 0.14)',
    iconBackground: 'rgba(12, 19, 28, 0.32)',
    iconBorderColor: 'rgba(220, 232, 246, 0.2)',
    iconColor: '#eef4fb',
    trackColor: 'rgba(37, 51, 69, 0.5)',
    fill: 'linear-gradient(90deg, #eef4fb, #aabed4)',
    actionBackground: 'rgba(13, 20, 30, 0.56)',
    accentColor: '#aabed4',
    accentSoft: 'rgba(170, 190, 212, 0.16)',
    accentMuted: 'rgba(204, 220, 238, 0.24)',
    surfaceBackground: 'rgba(44, 56, 72, 0.22)',
    onFillColor: '#16202c',
    edgeGlintStyle: makeEdgeGlint('rgba(235, 245, 255, 0.9)'),
    edgeGlintVerticalStyle: makeVerticalEdgeGlint('rgba(235, 245, 255, 0.9)'),
    sparkleStyle: makeSparkle('#eef4fb', 'rgba(170, 190, 212, 0.45)')
  }
};

const themePresetToFormat: Record<string, string> = {
  emerald: 'weekly_qualification_freeroll',
  blue: 'tournament_tier_qualification',
  orange: 'hybrid_freeroll_qualification',
  western_orange_details: 'western_orange_details',
  parchment: 'wanted_poster',
  purple: 'weekly_timeback_credit',
  cash: 'weekly_cash_back',
  neutral: 'legacy'
};

/** Gold of the $100K monthly promo pill, as a metal gradient. */
export const PROMO_GOLD_METAL =
  'linear-gradient(135deg, #fff1b8 0%, #f6cf62 24%, #c8901f 52%, #f5d77a 76%, #a86f16 100%)';

/**
 * Progress-bar fill for two-tier formats (EN-11291): the format's own fill (white to green)
 * while chasing the first tier, green to gold while chasing the top tier, gold metal once the
 * top tier is earned. Formats without tiers keep their theme fill.
 */
export const getTierProgressFill = (
  theme: PromoVisualTheme,
  tierState?: 'notQualified' | 'qualified' | 'bonusAchieved'
): string => {
  if (tierState === 'bonusAchieved') return PROMO_GOLD_METAL;
  if (tierState === 'qualified') return `linear-gradient(90deg, ${theme.accentColor}, #f6cf62)`;
  return theme.fill;
};

export const getPromoVisualTheme = (format: string, themePreset?: string): PromoVisualTheme => {
  const configuredFormat = themePreset ? themePresetToFormat[themePreset] : null;
  return (
    (configuredFormat && promoThemes[configuredFormat]) || promoThemes[format] || promoThemes.legacy
  );
};
